'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function SampleSizeCalculator() {
  const t = useTranslations("tools.sampleSizeCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [mode, setMode] = useState<'sample-size' | 'margin-error'>('sample-size');
  const [showResults, setShowResults] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Sample Size Calculator inputs
  const [sampleSizeInputs, setSampleSizeInputs] = useState({
    confidenceLevel: 95,
    marginError: 5,
    populationProportion: 50,
    populationSize: '',
  });

  // Margin of Error Calculator inputs
  const [marginErrorInputs, setMarginErrorInputs] = useState({
    confidenceLevel: 95,
    sampleSize: 100,
    populationProportion: 60,
    populationSize: '',
  });

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const zScores: { [key: number]: number } = {
    70: 1.04, 75: 1.15, 80: 1.28, 85: 1.44, 90: 1.645,
    95: 1.96, 98: 2.33, 99: 2.576, 99.9: 3.291,
    99.99: 3.891, 99.999: 4.417
  };

  const handleSampleSizeCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    const proportion = sampleSizeInputs.populationProportion / 100;
    const marginError = sampleSizeInputs.marginError / 100;

    if (proportion <= 0 || proportion >= 1) {
      setErrorMessage(t('errorProportionRange'));
      setShowResults(false);
      return;
    }

    if (marginError <= 0 || marginError > 0.5) {
      setErrorMessage(t('errorMarginRange'));
      setShowResults(false);
      return;
    }

    const z = zScores[sampleSizeInputs.confidenceLevel];
    const p = proportion;
    const mError = marginError;

    let sampleSize = (z * z * p * (1 - p)) / (mError * mError);

    if (sampleSizeInputs.populationSize && sampleSizeInputs.populationSize.trim() !== '') {
      const N = parseFloat(sampleSizeInputs.populationSize);
      if (N < 1) {
        setErrorMessage(t('errorPopulationMin'));
        setShowResults(false);
        return;
      }
      sampleSize = sampleSize / (1 + ((sampleSize - 1) / N));
    }

    const finalSampleSize = Math.ceil(sampleSize);

    setResults({
      type: 'sample-size',
      sampleSize: finalSampleSize,
      confidenceLevel: sampleSizeInputs.confidenceLevel,
      marginError: sampleSizeInputs.marginError,
      proportion: sampleSizeInputs.populationProportion,
      populationSize: sampleSizeInputs.populationSize,
    });
    setShowResults(true);
  };

  const handleMarginErrorCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    const proportion = marginErrorInputs.populationProportion / 100;
    const sampleSize = marginErrorInputs.sampleSize;

    if (sampleSize < 1) {
      setErrorMessage(t('errorSampleSizeMin'));
      setShowResults(false);
      return;
    }

    if (proportion <= 0 || proportion >= 1) {
      setErrorMessage(t('errorProportionRange'));
      setShowResults(false);
      return;
    }

    const z = zScores[marginErrorInputs.confidenceLevel];
    const p = proportion;
    const n = sampleSize;

    let marginError = z * Math.sqrt((p * (1 - p)) / n);

    if (marginErrorInputs.populationSize && marginErrorInputs.populationSize.trim() !== '') {
      const N = parseFloat(marginErrorInputs.populationSize);
      if (N < 1) {
        setErrorMessage(t('errorPopulationMin'));
        setShowResults(false);
        return;
      }
      if (n > N) {
        setErrorMessage(t('errorSampleExceedsPopulation'));
        setShowResults(false);
        return;
      }
      const fpc = Math.sqrt((N - n) / (N - 1));
      marginError = marginError * fpc;
    }

    setResults({
      type: 'margin-error',
      marginError: marginError * 100,
      confidenceLevel: marginErrorInputs.confidenceLevel,
      sampleSize: marginErrorInputs.sampleSize,
      proportion: marginErrorInputs.populationProportion,
      populationSize: marginErrorInputs.populationSize,
    });
    setShowResults(true);
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueHover: '#1d4ed8',
    green: '#10b981',
    greenBg: '#f0fdf4',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: '0 auto' },
    { fontSize: '1rem', maxWidth: '100%' },
    { fontSize: '1.125rem', maxWidth: '700px' },
    { fontSize: '1.25rem', maxWidth: '800px' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 4px 20px rgba(37, 99, 235, 0.1)',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const modeButtonsStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginBottom: '2rem', justifyContent: 'center', flexWrap: 'wrap' as const },
    {},
    {},
    {}
  );

  const modeBtnStyle = (isActive: boolean) => getResponsiveStyle(
    {
      border: `2px solid ${isActive ? colors.blue : '#cbd5e1'}`,
      background: isActive ? `linear-gradient(135deg, ${colors.blue} 0%, #7c3aed 100%)` : 'white',
      color: isActive ? 'white' : '#64748b',
      borderRadius: '0.75rem',
      cursor: 'pointer',
      fontWeight: 600,
      transition: 'all 0.3s ease',
      boxShadow: isActive ? '0 4px 12px rgba(37, 99, 235, 0.3)' : 'none',
    },
    { padding: '0.75rem 1rem', fontSize: '0.9rem' },
    { padding: '0.75rem 1.25rem', fontSize: '1rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' }
  );

  const calculatorCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formStyle = {
    display: 'grid',
    gap: '1.5rem',
  };

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const infoIconStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    background: '#e0f2fe',
    color: '#0284c7',
    borderRadius: '50%',
    fontSize: '0.75rem',
    fontWeight: 700,
    cursor: 'help',
    position: 'relative' as const,
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: 'white',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1.25rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' }
  );

  const helperTextStyle = getResponsiveStyle(
    { color: '#64748b', fontStyle: 'italic' },
    { fontSize: '0.8rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.85rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: `linear-gradient(135deg, ${colors.blue} 0%, #7c3aed 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
      marginTop: '0.5rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const errorMessageStyle = getResponsiveStyle(
    {
      background: '#fef2f2',
      border: '2px solid #fca5a5',
      color: '#991b1b',
      borderRadius: '0.75rem',
      marginTop: '1rem',
      display: errorMessage ? 'block' : 'none',
    },
    { padding: '0.875rem', fontSize: '0.9rem' },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '1rem',
      border: '2px solid #86efac',
      display: showResults ? 'block' : 'none',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: '#166534', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const resultPrimaryStyle = getResponsiveStyle(
    {
      background: `linear-gradient(135deg, ${colors.blue} 0%, #7c3aed 100%)`,
      color: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { color: 'rgba(255, 255, 255, 0.9)', marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { color: 'white', fontWeight: 700 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '2.5rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.25rem' }
  );

  const resultItemLabelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155' },
    { fontSize: '0.9rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const resultItemValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.blue },
    { fontSize: '1.1rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem',
  };

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary,
  };

  const faqItemStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
      border: '1px solid rgba(255, 200, 180, 0.15)',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const referenceStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '0.75rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 500,
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
          </div>

          <div style={modeButtonsStyle}>
            <button
              type="button"
              style={modeBtnStyle(mode === 'sample-size')}
              onClick={() => {
                setMode('sample-size');
                setShowResults(false);
                setErrorMessage('');
              }}
            >
              {t('modeSampleSize')}
            </button>
            <button
              type="button"
              style={modeBtnStyle(mode === 'margin-error')}
              onClick={() => {
                setMode('margin-error');
                setShowResults(false);
                setErrorMessage('');
              }}
            >
              {t('modeMarginError')}
            </button>
          </div>

          <div style={calculatorCardStyle}>
            {mode === 'sample-size' ? (
              <form style={formStyle} onSubmit={handleSampleSizeCalculate}>
                <div style={formGroupStyle}>
                  <label htmlFor="confidence-level" style={labelStyle}>
                    {t('confidenceLevel')}
                    <span style={infoIconStyle} title={t('confidenceLevelTooltip')}>?</span>
                  </label>
                  <select
                    id="confidence-level"
                    style={inputStyle}
                    value={sampleSizeInputs.confidenceLevel}
                    onChange={(e) => setSampleSizeInputs({...sampleSizeInputs, confidenceLevel: parseFloat(e.target.value)})}
                    required
                  >
                    <option value="70">70%</option>
                    <option value="75">75%</option>
                    <option value="80">80%</option>
                    <option value="85">85%</option>
                    <option value="90">90%</option>
                    <option value="95">95%</option>
                    <option value="98">98%</option>
                    <option value="99">99%</option>
                    <option value="99.9">99.9%</option>
                    <option value="99.99">99.99%</option>
                    <option value="99.999">99.999%</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="margin-error" style={labelStyle}>
                    {t('marginOfError')}
                    <span style={infoIconStyle} title={t('marginOfErrorTooltip')}>?</span>
                  </label>
                  <input
                    type="number"
                    id="margin-error"
                    style={inputStyle}
                    step="0.1"
                    min="0.1"
                    max="50"
                    value={sampleSizeInputs.marginError}
                    onChange={(e) => setSampleSizeInputs({...sampleSizeInputs, marginError: parseFloat(e.target.value)})}
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="population-proportion" style={labelStyle}>
                    {t('populationProportion')}
                    <span style={infoIconStyle} title={t('populationProportionTooltip')}>?</span>
                  </label>
                  <input
                    type="number"
                    id="population-proportion"
                    style={inputStyle}
                    step="0.1"
                    min="0.1"
                    max="99.9"
                    value={sampleSizeInputs.populationProportion}
                    onChange={(e) => setSampleSizeInputs({...sampleSizeInputs, populationProportion: parseFloat(e.target.value)})}
                    required
                  />
                  <span style={helperTextStyle}>{t('proportionHelper')}</span>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="population-size" style={labelStyle}>
                    {t('populationSize')}
                    <span style={infoIconStyle} title={t('populationSizeTooltip')}>?</span>
                  </label>
                  <input
                    type="number"
                    id="population-size"
                    style={inputStyle}
                    step="1"
                    min="1"
                    placeholder={t('populationSizePlaceholder')}
                    value={sampleSizeInputs.populationSize}
                    onChange={(e) => setSampleSizeInputs({...sampleSizeInputs, populationSize: e.target.value})}
                  />
                  <span style={helperTextStyle}>{t('populationSizeHelper')}</span>
                </div>

                <button type="submit" style={calculateButtonStyle}>
                  {t('calculateButton')}
                </button>
              </form>
            ) : (
              <form style={formStyle} onSubmit={handleMarginErrorCalculate}>
                <div style={formGroupStyle}>
                  <label htmlFor="confidence-level-2" style={labelStyle}>
                    {t('confidenceLevel')}
                    <span style={infoIconStyle} title={t('confidenceLevelTooltip')}>?</span>
                  </label>
                  <select
                    id="confidence-level-2"
                    style={inputStyle}
                    value={marginErrorInputs.confidenceLevel}
                    onChange={(e) => setMarginErrorInputs({...marginErrorInputs, confidenceLevel: parseFloat(e.target.value)})}
                    required
                  >
                    <option value="70">70%</option>
                    <option value="75">75%</option>
                    <option value="80">80%</option>
                    <option value="85">85%</option>
                    <option value="90">90%</option>
                    <option value="95">95%</option>
                    <option value="98">98%</option>
                    <option value="99">99%</option>
                    <option value="99.9">99.9%</option>
                    <option value="99.99">99.99%</option>
                    <option value="99.999">99.999%</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="sample-size-input" style={labelStyle}>
                    {t('sampleSizeInput')}
                    <span style={infoIconStyle} title={t('sampleSizeInputTooltip')}>?</span>
                  </label>
                  <input
                    type="number"
                    id="sample-size-input"
                    style={inputStyle}
                    step="1"
                    min="1"
                    value={marginErrorInputs.sampleSize}
                    onChange={(e) => setMarginErrorInputs({...marginErrorInputs, sampleSize: parseFloat(e.target.value)})}
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="population-proportion-2" style={labelStyle}>
                    {t('populationProportion')}
                    <span style={infoIconStyle} title={t('populationProportionTooltip')}>?</span>
                  </label>
                  <input
                    type="number"
                    id="population-proportion-2"
                    style={inputStyle}
                    step="0.1"
                    min="0.1"
                    max="99.9"
                    value={marginErrorInputs.populationProportion}
                    onChange={(e) => setMarginErrorInputs({...marginErrorInputs, populationProportion: parseFloat(e.target.value)})}
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="population-size-2" style={labelStyle}>
                    {t('populationSize')}
                    <span style={infoIconStyle} title={t('populationSizeTooltip')}>?</span>
                  </label>
                  <input
                    type="number"
                    id="population-size-2"
                    style={inputStyle}
                    step="1"
                    min="1"
                    placeholder={t('populationSizePlaceholder')}
                    value={marginErrorInputs.populationSize}
                    onChange={(e) => setMarginErrorInputs({...marginErrorInputs, populationSize: e.target.value})}
                  />
                  <span style={helperTextStyle}>{t('populationSizeHelper')}</span>
                </div>

                <button type="submit" style={calculateButtonStyle}>
                  {t('calculateMarginButton')}
                </button>
              </form>
            )}

            <div style={errorMessageStyle}>
              {errorMessage}
            </div>
          </div>

          <div style={resultsStyle}>
            <h3 style={resultsH3Style}>✅ {t('resultsTitle')}</h3>
            {results && results.type === 'sample-size' && (
              <div>
                <div style={resultPrimaryStyle}>
                  <div style={resultLabelStyle}>{t('requiredSampleSize')}</div>
                  <div style={resultValueStyle}>{results.sampleSize.toLocaleString()}</div>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('confidenceLevel')}</span>
                  <span style={resultItemValueStyle}>{results.confidenceLevel}%</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('marginOfError')}</span>
                  <span style={resultItemValueStyle}>±{results.marginError.toFixed(1)}%</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('populationProportion')}</span>
                  <span style={resultItemValueStyle}>{results.proportion.toFixed(1)}%</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('populationSize')}</span>
                  <span style={resultItemValueStyle}>
                    {results.populationSize && results.populationSize.trim() !== ''
                      ? parseInt(results.populationSize).toLocaleString()
                      : t('infinite')}
                  </span>
                </div>
              </div>
            )}
            {results && results.type === 'margin-error' && (
              <div>
                <div style={resultPrimaryStyle}>
                  <div style={resultLabelStyle}>{t('marginOfError')}</div>
                  <div style={resultValueStyle}>±{results.marginError.toFixed(2)}%</div>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('confidenceLevel')}</span>
                  <span style={resultItemValueStyle}>{results.confidenceLevel}%</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('sampleSizeInput')}</span>
                  <span style={resultItemValueStyle}>{results.sampleSize.toLocaleString()}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('populationProportion')}</span>
                  <span style={resultItemValueStyle}>{results.proportion.toFixed(1)}%</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('populationSize')}</span>
                  <span style={resultItemValueStyle}>
                    {results.populationSize && results.populationSize.trim() !== ''
                      ? parseInt(results.populationSize).toLocaleString()
                      : t('infinite')}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step5Title')}</strong> {t('step5Description')}
          </li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('insight1Title')}</strong> {t('insight1Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('insight2Title')}</strong> {t('insight2Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('insight3Title')}</strong> {t('insight3Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('insight4Title')}</strong> {t('insight4Description')}
          </li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>{t('formulaTitle')}</h3>
        <p style={paragraphStyle}>{t('formulaParagraph1')}</p>
        <p style={paragraphStyle}>{t('formulaParagraph2')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '2rem'}}>{t('parametersTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('param1Title')}</strong> {t('param1Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('param2Title')}</strong> {t('param2Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('param3Title')}</strong> {t('param3Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('param4Title')}</strong> {t('param4Description')}
          </li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <p style={referenceStyle}>
          1. {t('reference1')} <a href="https://www.surveysystem.com/sscalc.htm" style={linkStyle} target="_blank" rel="noopener noreferrer">surveysystem.com</a>
        </p>
        <p style={referenceStyle}>
          2. {t('reference2')} <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3775042/" style={linkStyle} target="_blank" rel="noopener noreferrer">NCBI</a>
        </p>
        <p style={referenceStyle}>
          3. {t('reference3')} <a href="https://www.statisticshowto.com/probability-and-statistics/find-sample-size/" style={linkStyle} target="_blank" rel="noopener noreferrer">Statistics How To</a>
        </p>
        <p style={referenceStyle}>
          4. {t('reference4')} <a href="https://www.checkmarket.com/sample-size-calculator/" style={linkStyle} target="_blank" rel="noopener noreferrer">Checkmarket</a>
        </p>
        <p style={referenceStyle}>
          5. {t('reference5')} <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6235923/" style={linkStyle} target="_blank" rel="noopener noreferrer">NCBI Power Analysis</a>
        </p>
      </section>
    </div>
  );
}